import { AxiosInstance, AxiosRequestConfig } from 'axios'
import { StaleTokenDetectorService } from 'src/services/stale-token-detector.service'
import { TokenService } from 'src/services/token.service'

const MAX_400_COUNT = 5

// Module scope, not closure scope: both axios instances install this interceptor,
// and per-instance refresh state let them race and persist a superseded refresh token.
let consecutive400Count = 0
let inFlightRefresh: Promise<string> | null = null

type RetryableRequestConfig = AxiosRequestConfig & { _retriedAfterRefresh?: boolean }

function refreshAccessTokenOnce(): Promise<string> {
  if (inFlightRefresh) return inFlightRefresh

  inFlightRefresh = (async () => {
    const tokenService = TokenService.getInstance()
    await tokenService.refreshAccessToken()
    const newToken = tokenService.getToken().value
    if (!newToken) {
      throw new Error('Token refresh resolved without a token')
    }
    return newToken
  })().finally(() => {
    inFlightRefresh = null
  })

  return inFlightRefresh
}

export function axiosRefreshToken(client: AxiosInstance) {
  // Lazy initialization: only get service instances when actually needed
  // This avoids "cannot access before initialization" errors
  let staleTokenDetector: ReturnType<typeof StaleTokenDetectorService.getInstance> | null = null

  function getStaleTokenDetector() {
    if (!staleTokenDetector) {
      staleTokenDetector = StaleTokenDetectorService.getInstance()
    }
    return staleTokenDetector
  }

  client.interceptors.response.use(
    (response) => {
      consecutive400Count = 0 // Reset count on success
      getStaleTokenDetector().resetErrorCounts() // Reset stale token detection on success
      return response
    },
    async (error) => {
      // Handle 404 errors for stale token detection
      if (error.response?.status === 404) {
        const endpoint = error.config?.url || 'unknown'
        const shouldLogout = getStaleTokenDetector().record404Error(endpoint)

        if (shouldLogout) {
          getStaleTokenDetector().forceLogout()
        }

        return Promise.reject(error)
      }

      let isAuthFailure = error.response?.status === 401

      if (error.response?.status === 400) {
        consecutive400Count++
        if (consecutive400Count < MAX_400_COUNT) {
          return Promise.reject(error)
        }
        // The threshold is a one-shot trigger. Left above the limit the counter
        // turns every later 400 into its own refresh, so a stuck poll rotates
        // the refresh token once per request for as long as it keeps failing.
        consecutive400Count = 0
        isAuthFailure = true
      }

      if (!isAuthFailure) {
        return Promise.reject(error)
      }

      const config = error.config as RetryableRequestConfig | undefined
      // One retry per request. Without this a request that 401s again after a
      // successful refresh loops forever, which is a failure path that never settles.
      if (!config || config._retriedAfterRefresh) {
        return Promise.reject(error)
      }

      let newToken: string
      try {
        newToken = await refreshAccessTokenOnce()
      } catch (refreshError) {
        console.error('Token refresh failed: ', refreshError)
        return Promise.reject(refreshError)
      }

      // The retry sits outside the try on purpose: a rejection from the
      // retried request is not a refresh failure and must not be logged as one.
      config._retriedAfterRefresh = true
      config.headers = { ...(config.headers ?? {}), authorization: `Bearer ${newToken}` }
      return client.request(config)
    },
  )
}
